'use client';

import { c as _c } from "react/compiler-runtime";
import { appendLPSourceToUrl } from '~/components/Link/src/LinkWithSource';
import { ListingCard } from '~/components/ListingCard';
import { Markdown } from '~/components/Markdown';
import { PromoBadge } from '~/components/PromoBadge';
import { getLovableUrlForBlueprint } from '~/constants';
import { OVERVIEW_EXPERIMENT_LP_SLUG, OVERVIEW_EXPERIMENT_PATH, OVERVIEW_EXPERIMENT_VARIANTS } from '~/constants/overviewExperiment';
import { useCurrencyContext } from '~/contexts/Currencies';
import { useOverviewExperimentVariant } from '~/hooks/useOverviewExperimentVariant';
import { usePathname } from '~/i18n/navigation';
import { trackBlueprintClicked } from '~/utils';
import { blueprintFromPrice } from '~/utils/blueprintHelpers/blueprintFromPrice';
import { joinPlaceNames } from '~/utils/blueprintHelpers/joinPlaceNames';
import styles from './TripCard.module.css';
import type { BlueprintCardProps } from './TripCard.types';
import { TripCardFooter } from './TripCardFooter';
import { TripCardPlaces } from './TripCardPlaces';
const withLp = (url: string, lp?: string) => {
  if (!lp) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}lp=${lp}`;
};
const BlueprintCard = t0 => {
  const $ = _c(63);
  const {
    trip,
    showPromoBadge
  } = t0;
  const {
    description,
    shortDescription,
    blueprintId,
    image,
    title,
    duration,
    pricePerNight,
    placesLinked,
    originPrice
  } = trip;
  const pathname = usePathname() || "";
  const {
    convertPriceInGBP
  } = useCurrencyContext();
  const variant = useOverviewExperimentVariant();
  let lovableUrl;
  let lp;
  let t1;
  let urlWithSource;
  if ($[0] !== blueprintId || $[1] !== pathname || $[2] !== title || $[3] !== variant) {
    lovableUrl = pathname === OVERVIEW_EXPERIMENT_PATH ? getLovableUrlForBlueprint(blueprintId) : undefined;
    lp = lovableUrl ? OVERVIEW_EXPERIMENT_LP_SLUG : undefined;
    let t2;
    if ($[8] !== blueprintId || $[9] !== lp || $[10] !== pathname || $[11] !== title) {
      const url = withLp(`/journey-template/${blueprintId}?title=${encodeURIComponent(title)}`, lp);
      t2 = appendLPSourceToUrl(pathname, url);
      $[8] = blueprintId;
      $[9] = lp;
      $[10] = pathname;
      $[11] = title;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    urlWithSource = t2;
    t1 = lovableUrl && variant === OVERVIEW_EXPERIMENT_VARIANTS.intermediate ? withLp(lovableUrl, lp) : undefined;
    $[0] = blueprintId;
    $[1] = pathname;
    $[2] = title;
    $[3] = variant;
    $[4] = lovableUrl;
    $[5] = lp;
    $[6] = t1;
    $[7] = urlWithSource;
  } else {
    lovableUrl = $[4];
    lp = $[5];
    t1 = $[6];
    urlWithSource = $[7];
  }
  const overviewUrl = t1;
  let t2;
  if ($[13] !== overviewUrl || $[14] !== urlWithSource) {
    t2 = overviewUrl ? {
      href: overviewUrl,
      isExternal: true,
      target: "_self" as const
    } : {
      href: urlWithSource
    };
    $[13] = overviewUrl;
    $[14] = urlWithSource;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const linkProps = t2;
  let t3;
  if ($[16] !== blueprintId || $[17] !== lovableUrl || $[18] !== lp) {
    t3 = lovableUrl ? {
      "data-ua-click": "blueprint_card_click",
      "data-lp-source": lp,
      "data-blueprint-id": blueprintId
    } : {};
    $[16] = blueprintId;
    $[17] = lovableUrl;
    $[18] = lp;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const trackingAttrs = t3;
  let t4;
  if ($[20] !== duration || $[21] !== originPrice || $[22] !== pricePerNight) {
    t4 = originPrice === undefined ? blueprintFromPrice(pricePerNight, duration) : originPrice;
    $[20] = duration;
    $[21] = originPrice;
    $[22] = pricePerNight;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const fromPrice = t4;
  let t5;
  if ($[24] !== convertPriceInGBP || $[25] !== fromPrice) {
    t5 = fromPrice ? convertPriceInGBP(fromPrice) : null;
    $[24] = convertPriceInGBP;
    $[25] = fromPrice;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  const price = t5;
  let t6;
  if ($[27] !== blueprintId || $[28] !== linkProps.href || $[29] !== title || $[30] !== trip.allTags || $[31] !== trip.tags) {
    t6 = () => trackBlueprintClicked({
      blueprintId,
      title,
      tags: trip.allTags ?? trip.tags,
      sectionTag: trip.allTags ? trip.tags[0]?.tagId : undefined,
      url: linkProps.href
    });
    $[27] = blueprintId;
    $[28] = linkProps.href;
    $[29] = title;
    $[30] = trip.allTags;
    $[31] = trip.tags;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  const handleClick = t6;
  let t7;
  if ($[33] !== image) {
    t7 = image?.responsiveImage ? [{
      data: image.responsiveImage
    }] : [];
    $[33] = image;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== showPromoBadge) {
    t8 = showPromoBadge ? <PromoBadge /> : null;
    $[35] = showPromoBadge;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t7 || $[38] !== t8) {
    t9 = <div className={styles.gallerySlot}><ListingCard.Gallery images={t7} tag={t8} /></div>;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== handleClick || $[41] !== linkProps || $[42] !== title || $[43] !== trackingAttrs) {
    t10 = <ListingCard.Title as="h3"><ListingCard.Link {...linkProps} {...trackingAttrs} onClick={handleClick}>{title}</ListingCard.Link></ListingCard.Title>;
    $[40] = handleClick;
    $[41] = linkProps;
    $[42] = title;
    $[43] = trackingAttrs;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] !== placesLinked) {
    t11 = joinPlaceNames(placesLinked);
    $[45] = placesLinked;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  let t12;
  if ($[47] !== t11) {
    t12 = <TripCardPlaces places={t11} />;
    $[47] = t11;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== description || $[50] !== shortDescription) {
    t13 = (shortDescription || description) && <Markdown className={styles.markdown}>{shortDescription || description}</Markdown>;
    $[49] = description;
    $[50] = shortDescription;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== duration || $[53] !== price) {
    t14 = <TripCardFooter price={price} nights={duration} />;
    $[52] = duration;
    $[53] = price;
    $[54] = t14;
  } else {
    t14 = $[54];
  }
  let t15;
  if ($[55] !== t10 || $[56] !== t12 || $[57] !== t13 || $[58] !== t14) {
    t15 = <ListingCard.Content><div className={styles.tripCardContentInner}>{t10}{t12}{t13}{t14}</div></ListingCard.Content>;
    $[55] = t10;
    $[56] = t12;
    $[57] = t13;
    $[58] = t14;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  let t16;
  if ($[60] !== t15 || $[61] !== t9) {
    t16 = <ListingCard variant="trip">{t9}{t15}</ListingCard>;
    $[60] = t15;
    $[61] = t9;
    $[62] = t16;
  } else {
    t16 = $[62];
  }
  return t16;
};
export { BlueprintCard };